import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Alert,
  LinearProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import TinyEditor from "../../../custom-ckeditor/TinyEditor";
import ItemService from "../../../apis/ItemService";
import {
  BLOOMS_TAXONOMY_SKILLS,
  normalizeSkillTypeValue,
} from "../constants/lessonMetadata";
import { normaliseTaxonomyOptions } from "../utils/taxonomy";
import QuizPage from "../../quiz/QuizPage";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const getItemIdFromAny = (res, fallback) =>
  res?.item?.Id ??
  res?.item?.id ??
  res?.item?.itemId ??
  res?.Id ??
  res?.id ??
  res?.itemId ??
  fallback?.Id ??
  fallback?.id ??
  fallback?.itemId ??
  null;

const RankingItemDetail = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const isNew = !itemId || itemId === "new";
  const location = useLocation();
  const clonedItem = location.state?.clonedItem || null;

  const [item, setItem] = useState({
    Type: "Ranking",
    Name: "",
    points: 1,
    skilltype: "",
    itemcat: "",
    Direction: "LTR",
    Title: "",
    Options: [{ id: 1, Value: "", Rationale: "" }],
  });

  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [isRtl, setIsRtl] = useState(false);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewRankingSelections, setPreviewRankingSelections] = useState([]);
  const [effectiveItemId, setEffectiveItemId] = useState(
    clonedItem ? null : isNew ? null : itemId
  );

  const detectIsArabic = (text) => /[\u0600-\u06FF]/.test(text);

  // 🟢 Load taxonomy + existing item (if editing)
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [taxonomyResponse, itemResponse] = await Promise.all([
          ItemService.fetchTaxonomy("arabic"),
          isNew ? Promise.resolve(null) : ItemService.fetchItemById(itemId),
        ]);

        setTaxonomyOptions(normaliseTaxonomyOptions(taxonomyResponse));

        let loaded;

        // 1️⃣ Clone Mode
        if (clonedItem) {
          loaded = {
            ...clonedItem,

            // Remove backend-linked fields
            id: undefined,
            Id: undefined,
            itemId: undefined,
            ItemId: undefined,
            PK: undefined,
            SK: undefined,
            GPK1: undefined,
            GSK1: undefined,
            json: undefined,
            Json: undefined,

            createdBy: "",
            updatedBy: "",
            version: "v1",

            // Name is already "(Copy)" from ItemLibrary → DO NOT add again
            Name: clonedItem.Name,

            Options: clonedItem.Options?.map((opt, idx) => ({
              id: idx + 1,
              Value: opt.Value || "",
              Rationale: opt.Rationale || "",
            })) ?? [{ id: 1, Value: "", Rationale: "" }],
          };

          setEffectiveItemId(null); // FORCE create mode
        }
        // 2️⃣ Edit mode
        else if (itemResponse?.item) {
          const existing = itemResponse.item;

          loaded = {
            Type: existing.Type || "Ranking",
            Name: existing.Name || "",
            points: existing.points ?? 1,
            skilltype: existing.skilltype || "",
            itemcat: existing.itemcat || "",
            Direction: existing.Direction || "LTR",
            Title: existing.Title || "",
            Options:
              existing.Options?.length > 0
                ? existing.Options.map((o, i) => ({
                    id: o.id ?? i + 1,
                    Value: o.Value ?? "",
                    Rationale: o.Rationale ?? "",
                  }))
                : [{ id: 1, Value: "", Rationale: "" }],
          };
        }
        // 3️⃣ New mode
        else {
          loaded = {
            Type: "Ranking",
            Name: "",
            points: 1,
            skilltype: "",
            itemcat: "",
            Direction: "LTR",
            Title: "",
            Options: [{ id: 1, Value: "", Rationale: "" }],
          };
        }

        setItem(loaded);
      } catch (err) {
        console.error("❌ Failed to load Ranking item:", err);
        setSaveError("Failed to load Ranking item.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [itemId, isNew, clonedItem]);

  // 🧭 Detect Arabic automatically
  useEffect(() => {
    const firstValue = item.Options?.[0]?.Value || "";
    if (firstValue) setIsRtl(detectIsArabic(firstValue));
  }, [item.Options]);

  const handleCloneFromEditor = useCallback(() => {
    if (!item) return;

    const clonedPayload = {
      ...item,

      // 🔥 remove identifiers
      id: undefined,
      Id: undefined,
      itemId: undefined,
      ItemId: undefined,
      PK: undefined,
      SK: undefined,
      GPK1: undefined,
      GSK1: undefined,

      // metadata reset
      Name: `${item.Name || "Untitled Ranking"} (Copy)`,
      createdBy: "",
      updatedBy: "",
      version: "v1",

      // JSON rebuilt on save
      json: undefined,
      Json: undefined,

      // 🔥 ensure clean option IDs
      Options: item.Options.map((opt, idx) => ({
        id: idx + 1,
        Value: opt.Value,
        Rationale: opt.Rationale || "",
      })),
    };

    navigate("/authoring/items/ranking/item/new", {
      state: { clonedItem: clonedPayload },
    });
  }, [item, navigate]);

  const handleOptionChange = (index, field, value) => {
    setItem((prev) => {
      const opts = [...prev.Options];
      opts[index] = { ...opts[index], [field]: value };
      return { ...prev, Options: opts };
    });
  };

  const addOption = () =>
    setItem((prev) => ({
      ...prev,
      Options: [
        ...prev.Options,
        { id: prev.Options.length + 1, Value: "", Rationale: "" },
      ],
    }));

  const deleteOption = (index) =>
    setItem((prev) => ({
      ...prev,
      Options: prev.Options.filter((_, i) => i !== index),
    }));

  // 🟢 Save (create/update)
  const handleSave = useCallback(async () => {
    if (!item.Name || !item.itemcat || !item.skilltype) {
      setSaveError("Please fill all required fields.");
      return;
    }

    const timestamp = new Date().toISOString();

    const payload = {
      PK: `itemcat#${item.itemcat};skilltype#${item.skilltype}`,
      SK: effectiveItemId ? `item#${effectiveItemId}` : undefined,
      createdBy: "",
      updatedBy: "",
      dateCreated: timestamp,
      dateUpdated: timestamp,
      GPK1: "item",
      GSK1: effectiveItemId ? `item#${effectiveItemId}` : undefined,

      itemcat: item.itemcat,
      skilltype: item.skilltype,
      Name: item.Name,

      Options: item.Options.map((opt, i) => ({
        id: i + 1,
        Rationale: opt.Rationale || "",
        Value: opt.Value || "",
      })),

      points: item.points,
      Title:
        item.Title ||
        "<div>Arrange the words to form the correct sentence</div>",
      Type: "Ranking",
      version: "v1",
    };

    try {
      setSaving(true);
      const response = await ItemService.saveItem(
        payload,
        effectiveItemId || undefined
      );

      setSaveSuccess(true);
      setSaveError(null);

      // After create → switch into edit mode
      const newId = getItemIdFromAny(response, payload);

      if (!effectiveItemId && newId) {
        setEffectiveItemId(newId);
      }
    } catch (err) {
      setSaveError(err?.message || "Failed to save item.");
    } finally {
      setSaving(false);
    }
  }, [item, effectiveItemId]);

  const jsonPreview = useMemo(() => JSON.stringify(item, null, 2), [item]);

  if (loading)
    return (
      <Paper sx={{ p: 3 }}>
        <LinearProgress />
        <Typography>Loading Ranking item...</Typography>
      </Paper>
    );

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        {/* === Header === */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label="Ranking" color="secondary" />
            <Typography variant="h6" fontWeight={600}>
              {isNew ? "Create Ranking Item" : "Edit Ranking Item"}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              disabled={!effectiveItemId} // 🔥 same rule as others
              onClick={handleCloneFromEditor}
            >
              Clone
            </Button>

            <Button
              variant="outlined"
              onClick={() => setPreviewDialogOpen(true)}
            >
              Preview
            </Button>

            <Button variant="outlined" onClick={() => setJsonDialogOpen(true)}>
              View JSON
            </Button>
          </Stack>
        </Stack>

        {saveSuccess && (
          <Alert severity="success">Item saved successfully!</Alert>
        )}
        {saveError && <Alert severity="error">{saveError}</Alert>}

        {/* === Basic Fields === */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Item Name"
              fullWidth
              required
              value={item.Name}
              onChange={(e) => setItem((p) => ({ ...p, Name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Points"
              type="number"
              fullWidth
              value={item.points}
              onChange={(e) =>
                setItem((p) => ({ ...p, points: Number(e.target.value) }))
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth required>
              <InputLabel>Skill Type</InputLabel>
              <Select
                value={item.skilltype}
                label="Skill Type"
                onChange={(e) =>
                  setItem((p) => ({
                    ...p,
                    skilltype: normalizeSkillTypeValue(e.target.value),
                  }))
                }
              >
                {BLOOMS_TAXONOMY_SKILLS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Item Category</InputLabel>
              <Select
                value={item.itemcat}
                label="Item Category"
                onChange={(e) =>
                  setItem((p) => ({ ...p, itemcat: e.target.value }))
                }
              >
                {taxonomyOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* === Prompt === */}
        <Divider />
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Instructions (Title)
          </Typography>
          <TinyEditor
            value={item.Title || ""}
            onChange={(v) => setItem((p) => ({ ...p, Title: v }))}
            placeholder="Write your question or instructions..."
            minHeight={250}
          />
        </Box>

        {/* === RTL Toggle === */}
        <FormControlLabel
          control={
            <Switch checked={isRtl} onChange={() => setIsRtl((p) => !p)} />
          }
          label="Right-to-left direction"
        />

        {/* === Options === */}
        <Divider />
        <Typography variant="subtitle1" fontWeight={600}>
          Ranking Items
        </Typography>
        {item.Options.map((opt, i) => (
          <Stack key={i} spacing={1} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label={`Item ${i + 1}`}
              value={opt.Value}
              onChange={(e) => handleOptionChange(i, "Value", e.target.value)}
              inputProps={{
                dir: isRtl ? "rtl" : "ltr",
                style: { textAlign: isRtl ? "right" : "left" },
              }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Rationale"
              value={opt.Rationale}
              onChange={(e) =>
                handleOptionChange(i, "Rationale", e.target.value)
              }
            />
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => deleteOption(i)}
              sx={{ alignSelf: "flex-end", mt: -1 }}
            >
              Remove
            </Button>
          </Stack>
        ))}
        <Button startIcon={<AddIcon />} onClick={addOption}>
          Add Item
        </Button>

        {/* === Save === */}
        <Stack direction="row" justifyContent="flex-end">
          <Button
            startIcon={effectiveItemId ? <SaveIcon /> : <AddIcon />}
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? effectiveItemId
                ? "Saving..."
                : "Creating..."
              : effectiveItemId
              ? "Save"
              : "Create"}
          </Button>
        </Stack>
      </Stack>

      {/* === JSON Dialog === */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Item JSON Preview</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "monospace",
              fontSize: 13,
            }}
          >
            {jsonPreview}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* === PREVIEW Dialog === */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Ranking Preview</DialogTitle>
        <DialogContent dividers>
          <QuizPage
            previewMode
            previewActivities={[
              {
                ...item,
                ContentId: "preview",
              },
            ]}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RankingItemDetail;
